import { useCallback, useEffect, useState } from "react";
import { LifeBuoy } from "lucide-react";

import {
  type SupportVisit,
  endSupportSession,
  getSupportVisit,
  minutesLeft,
  renewSupportSession,
} from "@/lib/supportSession";

/** Comfortably inside the token's 15' life, so a renewal that fails once still has a
 *  second chance before the technician notices anything. */
const RENEW_EVERY_MS = 8 * 60 * 1000;
const TICK_MS = 30 * 1000;

/**
 * The permanent "you are a visitor" bar (ADR-0081 S3).
 *
 * It is not decoration. Two things depend on it existing:
 *
 * - **Honesty.** A support session that looks identical to the customer's own operator
 *   session is exactly what made impersonation unacceptable (D6). Whoever is at this
 *   keyboard — and whoever walks past it — should be able to tell at a glance.
 * - **The visit surviving.** The access token is 15'; the visit runs to 8h. This is what
 *   renews, and a renewal that FAILS ends the visit locally instead of leaving a dead
 *   session pretending to work: the server refused because the visit is over (idled out,
 *   consent revoked, cap reached), and the honest response is to show the door.
 *
 * Renders nothing for a normal operator, so mounting it in the shell is free.
 */
export function SupportSessionBanner() {
  const [visit, setVisit] = useState<SupportVisit | null>(() => getSupportVisit());
  const [now, setNow] = useState(() => Date.now());

  const leave = useCallback(async () => {
    await endSupportSession();
    setVisit(null);
    window.location.assign("/login");
  }, []);

  useEffect(() => {
    if (!visit) return;
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    const renew = setInterval(() => {
      renewSupportSession().catch(() => void leave());
    }, RENEW_EVERY_MS);
    return () => {
      clearInterval(tick);
      clearInterval(renew);
    };
  }, [visit, leave]);

  if (!visit) return null;

  const left = minutesLeft(visit.expires_at, now);
  const who = visit.subject === "tray_support" ? "Supporto Tray" : "Partner";

  return (
    <div className="flex items-center gap-3 border-b border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-xs text-amber-900 dark:text-amber-200">
      <LifeBuoy className="h-3.5 w-3.5 shrink-0" />
      <span className="font-medium">
        Sessione di assistenza — {who}: {visit.display_name}
      </span>
      <span className="text-amber-800/80 dark:text-amber-200/70">
        {left > 0 ? `termina fra ${left} min` : "in scadenza"}
      </span>
      <button
        type="button"
        onClick={() => void leave()}
        className="ml-auto rounded border border-amber-500/50 px-2 py-0.5 font-medium hover:bg-amber-500/20"
      >
        Esci
      </button>
    </div>
  );
}
